import type { UniqueIdentifier } from '@dnd-kit/core'

import { UNTAGGED_VALUE } from '~publish/components/FilterByTag'
import type { ResultOf } from '~publish/graphql'

import type { IdeaCardIdea as Idea } from '../../IdeaCard/IdeaCard'
import type { GetIdeasBoard } from '../hooks/useBoardData'

export const UNASSIGNED_GROUP_NAME = 'Unassigned'
export const UNASSIGNED_GROUP_ID = 'unassigned'

export function compareIdeas(aIdea: Idea, bIdea: Idea): number {
  // Handle potential null positions
  const aPosition = aIdea.position ?? Number.MAX_SAFE_INTEGER
  const bPosition = bIdea.position ?? Number.MAX_SAFE_INTEGER

  // 1. Primary sort by position (ascending)
  if (aPosition < bPosition) {
    return -1
  }
  if (aPosition > bPosition) {
    return 1
  }

  // 2. Secondary sort: If positions are equal, sort by createdAt (newest first)
  return bIdea.createdAt - aIdea.createdAt
}

export function buildItemsObject(
  data: ResultOf<typeof GetIdeasBoard>,
  selectedTagIds: string[],
): {
  ideas: Idea[]
  itemsMap: Record<UniqueIdentifier, UniqueIdentifier[]>
} {
  const ideas = data.ideas.edges?.map((edge) => edge.node)

  // Filter displayed ideas based on selected tags, or show all if no tags selected
  const displayedIdeas = ideas.toSorted(compareIdeas).filter((idea) => {
    if (selectedTagIds.length === 0) return true

    const hasUntaggedFilter = selectedTagIds.includes(UNTAGGED_VALUE)
    const otherTagIds = selectedTagIds.filter((id) => id !== UNTAGGED_VALUE)

    const ideaHasNoTags = !idea.content.tags || idea.content.tags.length === 0
    const ideaHasSelectedTag = idea.content.tags?.some((tag) =>
      otherTagIds.includes(tag.id),
    )

    if (hasUntaggedFilter && otherTagIds.length > 0) {
      // Union: untagged OR has selected tags
      return ideaHasNoTags || ideaHasSelectedTag
    } else if (hasUntaggedFilter) {
      // Only untagged
      return ideaHasNoTags
    } else {
      // Only specific tags (current behavior)
      return ideaHasSelectedTag
    }
  })

  // Construct items with ideas belonging to specific groups
  const itemsMap = data.ideaGroups.reduce<
    Record<UniqueIdentifier, UniqueIdentifier[]>
  >(
    (acc, group) => {
      acc[group.id] = displayedIdeas
        .filter((idea) => idea.groupId === group.id)
        .map((idea) => idea.id)
      return acc
    },
    { [UNASSIGNED_GROUP_ID]: [] },
  )

  // Populate items with unassigned ideas
  itemsMap[UNASSIGNED_GROUP_ID] = displayedIdeas
    .filter((idea) => !idea.groupId)
    .map((idea) => idea.id)

  return { ideas, itemsMap }
}

export function fromNullable<T>(value: T | null | undefined): T | undefined {
  return value ?? undefined
}
